import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession, useAsync } from '../lib/session';
import { inr, short, today } from '../lib/fmt';
import type { AccountBalance } from '../lib/types';
import { Button, Card, Field, Input, Select, Modal, Note, PageHead, Tag,
         Tile, Tiles, Empty } from '../components/ui';
import { Loading, HeadSkeleton, TilesSkeleton, CardSkeleton, TableSkeleton } from '../components/skeleton';

interface Feed { accounts: AccountBalance[]; can_manage: boolean; low_floats: AccountBalance[] }

/** Head, the four totals, then a card of accounts for each group. */
function AccountsSkeleton() {
  return (
    <Loading label="Loading accounts">
      <HeadSkeleton actions={2} />
      <TilesSkeleton />
      {[0, 1].map((i) => (
        <CardSkeleton key={i} className="mb-5" sub={false}>
          <TableSkeleton rows={3} cols={5} />
        </CardSkeleton>
      ))}
    </Loading>
  );
}

export default function Accounts() {
  const { can } = useSession();
  const feed = useAsync<Feed>(() => api.get('/api/accounts'), []);
  const [transfer, setTransfer] = useState(false);
  const [count, setCount] = useState<AccountBalance | null>(null);
  const [edit, setEdit] = useState<AccountBalance | 'new' | null>(null);

  if (feed.loading) return <AccountsSkeleton />;
  if (feed.error) return <Note title="That did not load" tone="critical">{feed.error}</Note>;
  const d = feed.data!;
  const banks = d.accounts.filter((a) => a.kind !== 'cash');
  const boxes = d.accounts.filter((a) => a.kind === 'cash');
  const total = d.accounts.reduce((s, a) => s + a.balance, 0);

  return (
    <div>
      <PageHead eyebrow="Cash and bank" title="Accounts"
        dek="Every account in one place. A balance here is the sum of its transactions, so it can always be explained."
        actions={<>
          {can('cash:transfer') && <Button onClick={() => setTransfer(true)}>Move money</Button>}
          {d.can_manage && <Button variant="primary" onClick={() => setEdit('new')}>Add account</Button>}
        </>} />

      <Tiles>
        <Tile hero label="Total across all accounts" value={short(total)}
              sub={<span className="text-muted">{d.accounts.length} accounts</span>} />
        <Tile label="In bank" value={short(banks.reduce((s, a) => s + a.balance, 0))}
              sub={<span className="text-muted">{banks.length} bank accounts</span>} />
        <Tile label="In cash" value={short(boxes.reduce((s, a) => s + a.balance, 0))}
              sub={<span className="text-muted">{boxes.length} cash {boxes.length === 1 ? 'box' : 'boxes'}</span>} />
        <Tile label="Floats running low" value={String(d.low_floats.length)}
              sub={<span className="text-muted">
                {d.low_floats.length ? d.low_floats.map((f) => f.name).join(', ') : 'all topped up'}</span>} />
      </Tiles>

      {d.low_floats.length > 0 && (
        <Note title="Top up needed">
          {d.low_floats.map((f) => `${f.name} is at ${inr(f.balance)} against a float of ${inr(f.float_limit)}.`).join(' ')}
        </Note>
      )}

      {([['Bank accounts', banks], ['Cash boxes', boxes]] as const).map(([title, list]) =>
        list.length === 0 ? null : (
          <Card key={title} title={title} className="mb-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
              {list.map((a) => (
                <div key={a.id} className="border border-rule rounded p-4 flex flex-col gap-2">
                  <div className="flex items-baseline justify-between gap-3.5">
                    <span className="min-w-0"><b>{a.name}</b> <Tag>{a.code}</Tag></span>
                    <span className={`font-display text-[19px] font-semibold tnum whitespace-nowrap
                                      ${a.balance < 0 ? 'text-critical' : ''}`}>{inr(a.balance)}</span>
                  </div>
                  <p className="m-0 font-mono text-[11.5px] text-muted">
                    {a.holder ? `Held by ${a.holder}` : ''}
                    {a.custodian_name ? ` · custodian ${a.custodian_name}` : ''}
                    {a.bank_name ? ` · ${a.bank_name}` : ''}
                    {a.account_no ? ` · ${'•'.repeat(4)}${a.account_no.slice(-4)}` : ''}
                  </p>
                  {a.purpose && <p className="m-0 text-[12.5px] text-ink-2 leading-relaxed">{a.purpose}</p>}
                  <div className="flex flex-wrap items-center justify-between gap-3 mt-auto pt-2.5
                                  border-t border-rule-soft text-[11px] text-muted">
                    <span className="font-mono">{a.txn_count} entries · in {short(a.total_in)} · out {short(a.total_out)}</span>
                    <span className="flex gap-1.5">
                      <Link to={`/accounts/${a.id}`}
                        className="text-xs text-ink-2 no-underline hover:text-ink px-3 inline-flex items-center min-h-[44px] md:min-h-0 md:px-2 md:py-1">Statement</Link>
                      {a.kind === 'cash' && can('cash:count') &&
                        <Button size="sm" onClick={() => setCount(a)}>Count cash</Button>}
                      {d.can_manage && <Button size="sm" variant="ghost" onClick={() => setEdit(a)}>Edit</Button>}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}

      {!banks.some((b) => b.account_no) && (
        <Note title="Bank details not entered yet">
          Open each account and add the bank name, account number, IFSC and the opening balance as at
          1 April. Until the opening balances are in, the totals above only reflect what this system
          has recorded — which is why an account can show a negative figure.
        </Note>
      )}

      {transfer && <TransferModal accounts={d.accounts} onClose={() => setTransfer(false)}
                                  onDone={() => { setTransfer(false); feed.reload(); }} />}
      {count && <CountModal account={count} onClose={() => setCount(null)}
                            onDone={() => { setCount(null); feed.reload(); }} />}
      {edit && <AccountModal account={edit === 'new' ? null : edit} onClose={() => setEdit(null)}
                             onDone={() => { setEdit(null); feed.reload(); }} />}
    </div>
  );
}

function TransferModal(
  { accounts, onClose, onDone }:
  { accounts: AccountBalance[]; onClose: () => void; onDone: () => void },
) {
  const [from, setFrom] = useState(accounts[0]?.id ?? '');
  const [to, setTo] = useState(accounts[1]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [when, setWhen] = useState(today());
  const [mode, setMode] = useState('neft');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  return (
    <Modal title="Move money between accounts" wide onClose={onClose} confirmLabel="Transfer" busy={busy}
      onConfirm={async () => {
        setBusy(true); setErr('');
        try {
          await api.post('/api/transfers', {
            txn_date: when, from, to,
            amount: Math.round((Number(amount) || 0) * 100),
            mode, narration: note.trim() || null,
          });
          onDone();
        } catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); setBusy(false); }
      }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <Field label="From">
          <Select value={from} onChange={(e) => setFrom(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} — {inr(a.balance)}</option>)}
          </Select>
        </Field>
        <Field label="To">
          <Select value={to} onChange={(e) => setTo(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </Field>
        <Field label="Amount (₹)"><Input type="number" step="0.01" value={amount}
          onChange={(e) => setAmount(e.target.value)} /></Field>
        <Field label="Date"><Input type="date" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
        <Field label="Mode">
          <Select value={mode} onChange={(e) => setMode(e.target.value)}>
            {['neft','rtgs','imps','upi','cash','cheque'].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
          </Select>
        </Field>
        <Field label="Note"><Input value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Top up office float" /></Field>
      </div>
      {err && <p className="mt-3 text-[12.5px] text-critical">{err}</p>}
    </Modal>
  );
}

function CountModal(
  { account, onClose, onDone }: { account: AccountBalance; onClose: () => void; onDone: () => void },
) {
  const [counted, setCounted] = useState('');
  const [when, setWhen] = useState(today());
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const v = Math.round((Number(counted) || 0) * 100);
  const diff = v - account.balance;
  const hint = counted === '' ? `Book balance is ${inr(account.balance)}.`
    : diff === 0 ? 'Matches the book exactly.'
    : `${diff > 0 ? 'Over' : 'Short'} by ${inr(Math.abs(diff))} — an adjustment will be posted so the book matches the box.`;

  return (
    <Modal title={`Count ${account.name}`} onClose={onClose} confirmLabel="Record the count" busy={busy}
      onConfirm={async () => {
        setBusy(true); setErr('');
        try {
          await api.post('/api/cash-counts', {
            account: account.id, counted_on: when, counted: v, notes: notes.trim() || null,
          });
          onDone();
        } catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); setBusy(false); }
      }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <Field label="Counted on"><Input type="date" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
        <Field label="Cash in the box (₹)"><Input type="number" step="1" value={counted}
          onChange={(e) => setCounted(e.target.value)} placeholder="What the box actually holds" /></Field>
        <Field label="Note" span><Input value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Evening count" /></Field>
      </div>
      <p className="mt-3 font-mono text-[11.5px] text-muted">{hint}</p>
      {err && <p className="mt-2 text-[12.5px] text-critical">{err}</p>}
    </Modal>
  );
}

function AccountModal(
  { account, onClose, onDone }: { account: AccountBalance | null; onClose: () => void; onDone: () => void },
) {
  const [f, setF] = useState({
    code: account?.code ?? '', name: account?.name ?? '', kind: account?.kind ?? 'bank',
    holder: account?.holder ?? '', bank_name: account?.bank_name ?? '',
    account_no: account?.account_no ?? '', ifsc: account?.ifsc ?? '',
    purpose: account?.purpose ?? '', tally_ledger: account?.tally_ledger ?? '',
    opening_balance: account ? String(account.opening_balance / 100) : '',
    float_limit: account ? String(account.float_limit / 100) : '',
    opening_date: '2026-04-01',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  return (
    <Modal title={account ? `Edit ${account.name}` : 'Add an account'} wide onClose={onClose}
      confirmLabel={account ? 'Save' : 'Create'} busy={busy}
      onConfirm={async () => {
        setBusy(true); setErr('');
        try {
          await api.post('/api/accounts', {
            id: account?.id, ...f,
            code: f.code.trim().toUpperCase(),
            opening_balance: Math.round((Number(f.opening_balance) || 0) * 100),
            float_limit: Math.round((Number(f.float_limit) || 0) * 100),
            tally_ledger: f.tally_ledger.trim() || f.name.trim(),
          });
          onDone();
        } catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); setBusy(false); }
      }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <Field label="Short code"><Input value={f.code} onChange={set('code')} placeholder="MAIN" /></Field>
        <Field label="Name"><Input value={f.name} onChange={set('name')} placeholder="Main account" /></Field>
        <Field label="Kind">
          <Select value={f.kind} onChange={set('kind')}>
            {['bank','cash','wallet','card'].map((k) => <option key={k} value={k}>{k}</option>)}
          </Select>
        </Field>
        <Field label="Held by"><Input value={f.holder} onChange={set('holder')} placeholder="Whose name it is in" /></Field>
        <Field label="Bank"><Input value={f.bank_name} onChange={set('bank_name')} /></Field>
        <Field label="Account number"><Input value={f.account_no} onChange={set('account_no')} /></Field>
        <Field label="IFSC"><Input value={f.ifsc} onChange={set('ifsc')} /></Field>
        <Field label="Opening balance (₹)"><Input type="number" step="0.01"
          value={f.opening_balance} onChange={set('opening_balance')} /></Field>
        <Field label="As at"><Input type="date" value={f.opening_date} onChange={set('opening_date')} /></Field>
        <Field label="Cash float ceiling (₹)"><Input type="number" step="1"
          value={f.float_limit} onChange={set('float_limit')} /></Field>
        <Field label="Tally ledger name"><Input value={f.tally_ledger} onChange={set('tally_ledger')} /></Field>
        <Field label="What this account is for" span>
          <Input value={f.purpose} onChange={set('purpose')} />
        </Field>
      </div>
      {err && <p className="mt-3 text-[12.5px] text-critical">{err}</p>}
    </Modal>
  );
}
